import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { activities } from '../data/mock';
import { ArrowLeft, Clock, Users, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';

const ActiviteDetail = () => {
  const { slug } = useParams();
  const activity = activities.find(a => a.slug === slug);

  if (!activity) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold mb-4">Activité non trouvée</h1>
          <Link to="/activites" className="text-[#afab53] hover:underline">
            Retour aux activités
          </Link>
        </div>
      </div>
    );
  }

  const relatedActivities = activities
    .filter(a => a.category === activity.category && a.id !== activity.id)
    .slice(0, 3);

  return (
    <div className="min-h-screen pt-20">
      {/* Hero */}
      <section className="relative h-[70vh]">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${activity.imageUrl})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
        </div>
        <div className="relative h-full flex items-end">
          <div className="max-w-7xl mx-auto px-4 pb-16 w-full">
            <Link 
              to="/activites" 
              className="inline-flex items-center gap-2 text-white mb-6 hover:text-[#afab53] transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Retour aux activités</span>
            </Link>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">{activity.name}</h1>
            <p className="text-xl text-gray-200 max-w-3xl">{activity.description}</p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              <div>
                <h2 className="text-3xl font-bold mb-6 text-gray-900">Découvrez {activity.name}</h2>
                <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
                  <p>
                    {activity.name} est l'une des activités phares proposées par Nature Cévennes Frémyc. Situés au cœur du Parc National des Cévennes, nous vous offrons un cadre exceptionnel pour pratiquer cette activité dans les meilleures conditions.
                  </p>
                  <p>
                    Que vous soyez débutant ou expérimenté, notre équipe d'encadrants diplômés saura adapter la sortie à votre niveau et à vos attentes. La sécurité est notre priorité, tout le matériel nécessaire est fourni et vérifié régulièrement.
                  </p>
                  <p>
                    Les sites que nous utilisons sont sélectionnés pour leur beauté naturelle et leur accessibilité. Gorges du Tarn, de la Jonte, de la Dourbie, Grands Causses... Chaque sortie est une invitation à découvrir les merveilles de notre région.
                  </p>
                </div>
              </div>

              <div className="bg-gray-50 rounded-xl p-8">
                <h3 className="text-2xl font-bold mb-6 text-gray-900">Informations pratiques</h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#afab53] rounded-lg flex items-center justify-center flex-shrink-0">
                      <Users className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Public</h4>
                      <p className="text-gray-700">Accessible à tous à partir de 8 ans</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#afab53] rounded-lg flex items-center justify-center flex-shrink-0">
                      <Clock className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Durée</h4>
                      <p className="text-gray-700">Demi-journée ou journée complète</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#afab53] rounded-lg flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Lieu de rendez-vous</h4>
                      <p className="text-gray-700">{activity.lieu}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#afab53] rounded-lg flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-bold text-xl">€</span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Tarif</h4>
                      <p className="text-gray-700">{activity.tarif.toFixed(2)} €</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 space-y-6">
                <div className="bg-gradient-to-br from-[#afab53] to-[#8a8742] rounded-xl p-8 text-white">
                  <h3 className="text-2xl font-bold mb-4">Réservez cette activité</h3>
                  <p className="mb-6 text-white/90">
                    Contactez-nous pour réserver ou obtenir plus d'informations sur cette activité.
                  </p>
                  <Link to="/contact/">
                    <Button className="w-full bg-white text-[#afab53] hover:bg-gray-100 mb-3">
                      Demander un devis
                    </Button>
                  </Link>
                  <div className="space-y-2 text-sm text-white/80">
                    <p>Tél : 06 58 13 48 48</p>
                    <p>Tél : 06 81 61 63 50</p>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-xl p-6">
                  <h4 className="font-semibold mb-4 text-gray-900">À prévoir</h4>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-start gap-2">
                      <span className="text-[#afab53] mt-1">•</span>
                      <span>Chaussures de sport fermées</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#afab53] mt-1">•</span>
                      <span>Vêtements adaptés à l'activité</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#afab53] mt-1">•</span>
                      <span>Bouteille d'eau</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#afab53] mt-1">•</span>
                      <span>Crème solaire et casquette</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Activities */}
      {relatedActivities.length > 0 && (
        <section className="py-16 px-4 bg-gray-50">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-3xl font-bold mb-12 text-gray-900">Activités similaires</h2>
            <div className="grid md:grid-cols-3 gap-8">
              {relatedActivities.map((related) => (
                <Link
                  key={related.id}
                  to={`/activites/${related.slug}`}
                  className="group bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={related.imageUrl} 
                      alt={related.name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold mb-2 text-gray-900">{related.name}</h3>
                    <p className="text-gray-600 text-sm">{related.description}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default ActiviteDetail;